class VariantOption extends HTMLElement {
  constructor() {
    super();
    this.changeEvent = new CustomEvent("variant-option:changed", {
      bubbles: true,
    });
    this.isActive = this.classList.contains("radio-active");
    this._value = this.getAttribute("value");
  }

  setActive() {
    if (!this.isActive) {
      this.isActive = true;
      this.classList.add("radio-active");
      this.dispatchEvent(this.changeEvent);
    }
  }

  removeActive() {
    this.isActive = false;
    this.classList.remove("radio-active");
  }

  connectedCallback() {
    this.addEventListener("click", this.setActive);
  }
}

class VariantOptionGroup extends HTMLElement {
  constructor() {
    super();
    this.changeEvent = new CustomEvent("variant-option-group:changed", {
      bubbles: true,
    });
    this.activeNode = this.querySelector(".radio-active");
  }

  cacheOptions() {
    this.options = {};

    Array.from(this.querySelectorAll("variant-option")).forEach((node) => {
      this.options[node._value] = {
        element: node,
        value: node._value,
      };
    });
  }

  catchVariantChange() {
    this.addEventListener("variant-option:changed", (event) => {
      this.activeNode = this.querySelector(".radio-active");
      if (this.activeNode) {
        this.activeNode.removeActive();
      }

      this.activeNode = event.target;
      this.dispatchEvent(this.changeEvent);
    });
  }

  connectedCallback() {
    this.cacheOptions();
    this.catchVariantChange();
  }
}

class VariantSelection extends HTMLElement {
  constructor() {
    super();
    this.changeEvent = new CustomEvent("variant-selection:changed", {
      bubbles: true,
    });
    this.variantGroupOptions = Array.from(
      this.querySelectorAll("variant-option-group")
    );
    this.activeVariantTitle;
    this.activeGroups = [];
  }

  registerOptionChange() {
    this.addEventListener("variant-option-group:changed", (event) => {
      this.buildActiveVariantTitle();

      if (!this.variantsByTitleJSON[this.activeVariantTitle]) {
        var lastUpdatedOptionName = event.target.getAttribute("name");
        var hasUpdatedOneOption = false;
        this.variantGroupOptions.forEach((elem) => {
          if (elem.getAttribute("name") != lastUpdatedOptionName) {
            if (!hasUpdatedOneOption) {
              elem.querySelector("variant-option").click();
              hasUpdatedOneOption = true;
            }
          }
        });
        return;
      }

      this.dispatchEvent(this.changeEvent);
    });
  }

  buildActiveVariantTitle() {
    this.activeVariantTitle = this.variantGroupOptions
      .map((elem) => {
        return elem.activeNode._value;
      })
      .join(" / ");
  }

  updateId(id) {
    this.querySelector('input[name="id"]').value = id;
    var url = new URL(window.location.href);

    if (id) {
      url.searchParams.set("variant", id);
    } else {
      url.searchParams.delete("variant");
    }

    window.history.replaceState({}, "", url.href);
  }

  connectedCallback() {
    this.registerOptionChange();
    this.buildActiveVariantTitle();
    this.dispatchEvent(this.changeEvent);
  }
}

class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.productJSON = {}; // populated on load from DOM, via @getProductJSON
    this.variantsByTitleJSON; // populated from productJSON. Allows us to get variant objects directly by their title
    this.current_variant = {}; // populated on init, via. @getActiveVariant. If false, means that there's no active variant.
    this.VariantSelection = this.querySelector("variant-selection");
    this.cart = document.querySelector("cart-drawer");

    this.changeEvent = new CustomEvent("variant:changed", { bubbles: true });
  }

  getProductJSON() {
    if (this.querySelector("[data-product-json]")) {
      this.productJSON = JSON.parse(
        this.querySelector("[data-product-json]").innerText
      );
    }
  }

  mapVariantsByTitle() {
    /* input:

      product json array:
      variants = [
        { "id": ..., "price"... "title": "Variant title"}
      ]

      ==> what we want:

      variants = {
        "Variant Title": variant obj,
        "Variant Title 2": variant obj,
      }

      */

    /* prevent building it */
    if (!this.variantsByTitleJSON) {
      this.variantsByTitleJSON = {};
      if (this.productJSON && this.productJSON.variants) {
        this.productJSON.variants.filter((variant) => {
          this.variantsByTitleJSON[variant.title] = variant;
        });
      }
    }

    this.VariantSelection.variantsByTitleJSON = this.variantsByTitleJSON;
  }

  listenForVariantChange() {
    this.addEventListener("variant-selection:changed", this.onVariantChange);
  }

  onVariantChange(event) {
    if (this.VariantSelection.activeVariantTitle) {
      this.setCurrentVariant(true);
    }
  }

  setCurrentVariant(notOnLoad) {
    this.current_variant =
      this.variantsByTitleJSON[this.VariantSelection.activeVariantTitle];
    this.dispatchEvent(this.changeEvent);

    if (notOnLoad) {
      this.refreshDOM();
    }
  }

  replaceGalleryImages(sectionContent) {
    let mainImageQuery = "#Product-gallery-main .productpage-media__image img";
    let mainThumbImageQuery = "#Product-gallery-thumb .swipeable-item img";

    let mainImage = this.freshDOM.querySelector(mainImageQuery);
    let mainThumbImage = this.freshDOM.querySelector(mainThumbImageQuery);

    if (mainImage)
      document.querySelector(mainImageQuery).replaceWith(mainImage);
    if (mainThumbImage)
      document.querySelector(mainThumbImageQuery).replaceWith(mainThumbImage);

    // Scroll to first item
    const swipers = document.querySelectorAll(
      "#Product-gallery-main, #Product-gallery-thumb"
    );
    swipers.forEach((swiper) => {
      swiper.thumbnailCallback(0);
    });
  }

  _nodeUpdatePrices() {
    document
      .querySelectorAll(".productpage__pricing")
      .forEach(
        (pricing, index) =>
          (pricing.innerHTML = this.freshDOM.querySelectorAll(
            ".productpage__pricing"
          )[index].innerHTML)
      );
    document.querySelector(".productpage__submit").innerHTML =
      this.freshDOM.querySelector(".productpage__submit").innerHTML;
  }

  _nodeUpdateProductOptions() {
    this.variantOptionGroupItems = this.freshDOM.querySelectorAll(
      "variant-selection variant-option-group"
    );

    Array.from(
      this.querySelectorAll("variant-selection variant-option-group")
    ).forEach((elem, index) => {
      elem.innerHTML = this.variantOptionGroupItems[index].innerHTML;
    });
  }

  _nodeUpdateOnSaleAttribute() {
    let newAttributeValue = this.freshDOM.querySelector(
      ".productpage[data-on-sale]"
    ).dataset.onSale;

    document
      .querySelector(".productpage[data-on-sale]")
      .setAttribute("data-on-sale", newAttributeValue);
  }

  _nodeUpdateUnitPrices() {
    if (document.querySelector(".productpage .unit-pricing")) {
      document
        .querySelectorAll(".productpage .unit-pricing")
        .forEach((elem) => {
          elem.innerHTML =
            this.freshDOM.querySelector(".unit-pricing").innerHTML;
        });
    }
  }

  updateStickyATC() {
    document.querySelector(".pdp__sticky-atc__content").innerHTML =
      this.freshDOM.querySelector(".pdp__sticky-atc__content").innerHTML;
  }

  refreshDOM() {
    this.VariantSelection.updateId(this.current_variant.id);
    this.section_id = this.closest(".shopify-section")
      .getAttribute("id")
      .replace("shopify-section-", "");

    fetch(
      location.pathname +
        "?variant=" +
        this.current_variant.id +
        "&section_id=" +
        this.section_id
    )
      .then((res) => {
        return res.text();
      })
      .then((resText) => {
        var domParser = new DOMParser();
        this.freshDOM = domParser.parseFromString(resText, "text/html");

        this._nodeUpdatePrices();
        this._nodeUpdateProductOptions();
        this._nodeUpdateOnSaleAttribute();
        this._nodeUpdateUnitPrices();
        this.replaceGalleryImages();
        this.updateStickyATC();
      });
  }

  onFormSubmit() {
    this.dispatchEvent(new CustomEvent("form:atc"));
  }

  registerFormSubmit() {
    this.form = this.querySelector("form");
    this.addEventListener("submit", (e) => {
      e.preventDefault();
      this.onFormSubmit();
    });
  }

  connectedCallback() {
    this.getProductJSON();
    this.mapVariantsByTitle();
    this.listenForVariantChange();
    this.setCurrentVariant();
    this.registerFormSubmit();
  }
}

customElements.define("variant-option", VariantOption);
customElements.define("variant-option-group", VariantOptionGroup);
customElements.define("variant-selection", VariantSelection);
customElements.define("product-form", ProductForm);

class ProductPageGrid extends HTMLElement {
  constructor() {
    super();
  }

  get headingAreaHeight() {
    return this.headingArea
      ? this.headingArea.getBoundingClientRect().height
      : 0;
  }

  get productFormHeight() {
    return this.productForm
      ? this.productForm.getBoundingClientRect().height
      : 0;
  }

  changeStickyTop(ele) {
    ele.style.setProperty("--sticky-top", this.headingAreaHeight + "px");
  }

  changeMargin(ele, val, direction) {
    let margin = "margin" + direction;
    ele.style[margin] = "-" + val + "px";
  }

  collectNodes() {
    this.productForm = this.querySelector(".productpage__form");
    this.headingArea = this.querySelector(".productpage-heading-area");
  }

  updateNodes() {
    if (window.innerWidth < 768) return;

    this.changeStickyTop(this.querySelector(".productpage__form"));
    this.productForm.style.marginTop = "-" + this.productFormHeight + "px";
    this.headingArea.style.marginBottom = this.productFormHeight + "px";

    var gall = this.querySelector(".productpage__media-gallery");
    var mainDropdowns = this.querySelector(".productpage-main-dropdowns");

    if (gall && mainDropdowns) {
      var differenceHeight =
        this.productForm.offsetHeight +
        this.headingArea.offsetHeight -
        gall.offsetHeight;

      if (differenceHeight > 0) {
        mainDropdowns.style.marginTop = "-" + differenceHeight + "px";
      }
    }

    [this.headingArea, this.productForm].forEach((node) => {
      node.classList.add("productpage-grid__item--sticky");
    });
  }

  reloadCalcs() {
    this.collectNodes();
    this.updateNodes();
  }

  connectedCallback() {
    this.collectNodes();
    this.updateNodes();
  }
}

customElements.define("productpage-grid", ProductPageGrid);

class WDNDisclosure extends HTMLElement {
  constructor() {
    super();
  }

  get getTrigger() {
    return (
      document.querySelector(this.getAttribute("trigger")) ||
      this.querySelector("[data-disclosure-trigger]")
    );
  }

  bindEvents() {
    if (this.getTrigger) {
      this.getTrigger.addEventListener("click", () => {
        this.focus();
      });
    }
  }

  connectedCallback() {
    this.content = this.querySelector("[data-disclosure-content]");
    this.bindEvents();
  }
}

customElements.define("wdn-disclosure", WDNDisclosure);

(function () {
  var pricematchContent = document.querySelector("[data-pricematch-content]");

  var pricematchOpen = function () {
    if (document.getSelection().toString().length > 0) {
      if (
        document
          .getSelection()
          .anchorNode.parentElement.classList.contains("productpage-heading")
      ) {
        pricematchContent.classList.add("active");
        document.addEventListener("click", pricematchClose);
        document.removeEventListener("selectionchange", pricematchOpen);
      }
    }
  };

  var pricematchClose = function () {
    /* keep open as long as we have selection. */
    if (document.getSelection().toString() == "") {
      pricematchContent.classList.remove("active");
      document.removeEventListener("click", pricematchClose);
      document.addEventListener("selectionchange", pricematchOpen);
    }
  };

  document
    .querySelector("[data-pricematch-close]")
    .addEventListener("click", (elem) => {
      pricematchContent.classList.remove("active");
      document.removeEventListener("click", pricematchClose);
      document.addEventListener("selectionchange", pricematchOpen);
    });

  document.addEventListener("selectionchange", pricematchOpen);
})();

class ModalUSPContent extends HTMLElement {
  constructor() {
    super();

    this.loaded = false;
  }

  fetchContent() {
    if (this.loaded) {
      return;
    }

    this.sectionId = this.getAttribute("data-section");
    this.pageHandle = this.getAttribute("data-page");
    fetch(
      Shopify.routes.root +
        "pages/" +
        this.pageHandle +
        "?section_id=" +
        this.sectionId
    )
      .then((res) => res.text())
      .then((txt) => {
        let section = new DOMParser()
          .parseFromString(txt, "text/html")
          .querySelector(".shopify-section");
        this.innerHTML = section.innerHTML;
        this.loaded = true;
      });
  }
}

customElements.define("modalusp-content", ModalUSPContent);
